import React, { useState } from 'react';
import { jobsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Upload, FileSpreadsheet, CheckCircle, AlertTriangle, XCircle, Loader2, ChevronRight,
} from 'lucide-react';
import RequireCap from '../components/auth/RequireCap';

function FileDrop({ label, accept, file, onFile, testId }) {
  const onChange = (e) => {
    const f = e.target.files[0];
    if (f) onFile(f);
  };
  return (
    <label className="block cursor-pointer" data-testid={testId}>
      <div className={`border-2 border-dashed rounded-lg p-6 text-center transition ${file ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'}`}>
        <FileSpreadsheet className={`w-8 h-8 mx-auto mb-2 ${file ? 'text-emerald-600' : 'text-slate-400'}`} />
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <p className="text-xs text-slate-500 mt-1">
          {file ? file.name : `Click to choose ${accept}`}
        </p>
        <input type="file" accept={accept} className="hidden" onChange={onChange} data-testid={`${testId}-input`} />
      </div>
    </label>
  );
}

function Bucket({ title, count, color, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <Badge className={color}>{count}</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

export default function JobImportTab() {
  const [jobFile, setJobFile] = useState(null);
  const [billFile, setBillFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const runPreview = async () => {
    if (!jobFile) { toast.error('Choose the Job File (.xlsx)'); return; }
    if (!billFile) { toast.error('Choose the Bill Codes CSV'); return; }
    setUploading(true);
    setPreview(null);
    setApplyResult(null);
    setErrorMsg('');
    try {
      const fd = new FormData();
      fd.append('job_file', jobFile);
      fd.append('bill_codes_file', billFile);
      const { data } = await jobsAPI.importPreview(fd);
      setPreview(data);
      toast.success('Preview ready. Review buckets before confirming.');
    } catch (e) {
      const msg = e.response?.data?.detail || e.userMessage || 'Preview failed';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const confirm = async () => {
    if (!preview?.preview_id) return;
    setApplying(true);
    setErrorMsg('');
    try {
      const { data } = await jobsAPI.importConfirm({ preview_id: preview.preview_id });
      setApplyResult(data);
      setPreview(null);
      setJobFile(null);
      setBillFile(null);
      toast.success(`Import complete — ${data.created_count} created, ${data.updated_count} updated`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.userMessage || 'Import failed';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  };

  const totals = preview?.totals || {};

  return (
    <RequireCap module="jobs" cap="add">
      <div className="space-y-6" data-testid="job-import-tab">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" /> WinTeam Jobs Import
            </CardTitle>
            <CardDescription>
              Upload two WinTeam exports. They&apos;re joined on <span className="font-mono text-xs">JobNumber</span>.
              Nothing is written until you confirm the preview.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileDrop
                label="Job File (.xlsx)"
                accept=".xlsx"
                file={jobFile}
                onFile={setJobFile}
                testId="job-file-drop"
              />
              <FileDrop
                label="Bill Codes By Job (.csv)"
                accept=".csv"
                file={billFile}
                onFile={setBillFile}
                testId="bill-codes-file-drop"
              />
            </div>

            <div className="mt-4 flex items-center gap-3">
              <Button onClick={runPreview} disabled={uploading || !jobFile || !billFile} data-testid="job-import-preview-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Parsing…</> : 'Run Dry-Run Preview'}
              </Button>
              {preview && (
                <span className="text-xs text-slate-500">
                  Preview cached for 30 minutes. Confirm below to apply.
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {applyResult && (
          <Alert className="border-emerald-300 bg-emerald-50">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <AlertDescription>
              <div className="font-semibold text-emerald-800">Import complete</div>
              <div className="text-sm text-emerald-700 mt-1">
                {applyResult.created_count} jobs created · {applyResult.updated_count} updated ·
                {' '}{applyResult.rate_lines_written} rate lines written
              </div>
            </AlertDescription>
          </Alert>
        )}

        {errorMsg && (
          <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 mb-4" data-testid="job-import-error">
            <span>{errorMsg}</span>
          </div>
        )}

        {preview && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card><CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-slate-400">Parsed jobs</p>
                <p className="text-2xl font-semibold">{totals.parsed_jobs || 0}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-slate-400">Parsed rate rows</p>
                <p className="text-2xl font-semibold">{totals.parsed_bill_rows || 0}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-slate-400">Will write</p>
                <p className="text-2xl font-semibold">{(totals.will_create || 0) + (totals.will_update || 0)}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-slate-400">Flagged</p>
                <p className="text-2xl font-semibold text-amber-600">{(totals.excluded_internal || 0) + (totals.needs_review || 0)}</p>
              </CardContent></Card>
            </div>

            <Tabs defaultValue="create" className="space-y-4">
              <TabsList>
                <TabsTrigger value="create" data-testid="preview-tab-create">
                  Will create <Badge className="ml-2 bg-emerald-100 text-emerald-800">{totals.will_create || 0}</Badge>
                </TabsTrigger>
                <TabsTrigger value="update" data-testid="preview-tab-update">
                  Will update <Badge className="ml-2 bg-blue-100 text-blue-800">{totals.will_update || 0}</Badge>
                </TabsTrigger>
                <TabsTrigger value="excluded" data-testid="preview-tab-excluded">
                  Excluded (internal) <Badge className="ml-2 bg-slate-200 text-slate-700">{totals.excluded_internal || 0}</Badge>
                </TabsTrigger>
                <TabsTrigger value="review" data-testid="preview-tab-review">
                  Needs review <Badge className="ml-2 bg-amber-100 text-amber-800">{totals.needs_review || 0}</Badge>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="create">
                <BucketTable rows={preview.will_create} type="create" />
              </TabsContent>
              <TabsContent value="update">
                <BucketTable rows={preview.will_update} type="update" />
              </TabsContent>
              <TabsContent value="excluded">
                <ExcludedTable rows={preview.excluded_internal} />
              </TabsContent>
              <TabsContent value="review">
                <ReviewTable rows={preview.needs_review} />
              </TabsContent>
            </Tabs>

            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setPreview(null)}>Discard preview</Button>
              <Button onClick={confirm} disabled={applying} data-testid="job-import-confirm-btn">
                {applying
                  ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Applying…</>
                  : <>Confirm import <ChevronRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </>
        )}
      </div>
    </RequireCap>
  );
}

function BucketTable({ rows, type }) {
  if (!rows?.length) {
    return <p className="text-sm text-slate-400 py-6 text-center">No rows in this bucket.</p>;
  }
  return (
    <Card><CardContent className="p-0">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Job Number</TableHead>
          <TableHead>Client</TableHead>
          <TableHead>Description</TableHead>
          <TableHead className="text-right">Rate Lines</TableHead>
          {type === 'update' && <TableHead>Existing ID</TableHead>}
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={`${r.job_number}-${i}`}>
              <TableCell className="font-mono text-xs">{r.job_number}</TableCell>
              <TableCell>{r.customer_name || '—'}</TableCell>
              <TableCell className="max-w-md truncate">{r.description || '—'}</TableCell>
              <TableCell className="text-right">{r.rate_lines || 0}</TableCell>
              {type === 'update' && <TableCell className="font-mono text-xs text-slate-400">{r.existing_id?.slice(0, 12)}…</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent></Card>
  );
}

function ExcludedTable({ rows }) {
  if (!rows?.length) {
    return <p className="text-sm text-slate-400 py-6 text-center">No internal / administrative rows found.</p>;
  }
  return (
    <Card><CardContent className="p-0">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Job Number</TableHead>
          <TableHead>Reason</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Source Customer</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={`${r.job_number}-${i}`}>
              <TableCell className="font-mono text-xs">{r.job_number}</TableCell>
              <TableCell><Badge variant="outline">{r.reason}</Badge></TableCell>
              <TableCell className="max-w-md truncate">{r.description || '—'}</TableCell>
              <TableCell>{r.customer_name || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent></Card>
  );
}

function ReviewTable({ rows }) {
  if (!rows?.length) {
    return <p className="text-sm text-slate-400 py-6 text-center">Everything resolved cleanly — no manual review needed.</p>;
  }
  return (
    <Card><CardContent className="p-4 space-y-3">
      {rows.map((r, i) => (
        <div key={`${r.job_number}-${i}`} className="p-3 bg-amber-50 border border-amber-200 rounded-md">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="font-mono text-xs">{r.job_number}</span>
            <Badge variant="outline" className="text-xs">{r.reason}</Badge>
            {r.customer_name && <span className="text-xs text-slate-500">{r.customer_name}</span>}
          </div>
          {r.reason === 'unresolved_client' && (
            <p className="text-xs text-slate-600">
              Client &ldquo;{r.customer_name}&rdquo; (# {r.customer_number}) not found in Clients. Import the Customer Master or create the client, then re-run.
            </p>
          )}
          {r.reason === 'rate_line_issues' && r.issues && (
            <ul className="mt-1 text-xs text-slate-600 space-y-0.5">
              {r.issues.map((iss, j) => (
                <li key={j}>
                  · <span className="font-medium">{iss.invoice_desc || '(blank invoice desc)'}</span>
                  {iss.issues?.includes('unmatched_billing_item') && <span className="text-amber-700"> — unmatched billing item</span>}
                  {iss.issues?.includes('ambiguous_supervisor') && <span className="text-amber-700"> — ambiguous supervisor &ldquo;{iss.supervisor}&rdquo;</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </CardContent></Card>
  );
}
